import { api } from './api';

// Types
export interface School {
  _id: string;
  name: string;
  slug: string;
  description: string;
  shortDescription?: string;
  location: string;
  country: string;
  city: string;
  address?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
  phone?: string;
  email?: string;
  website?: string;
  rating: number;
  totalReviews: number;
  isActive: boolean;
  isFeatured: boolean;
  yogaAllianceCertified: boolean;
  yogaAllianceCertification?: {
    registrationNumber: string;
    certificationType: 'RYS-200' | 'RYS-300' | 'RYS-500' | 'RYS-200/300' | 'RYS-200/500' | 'RYS-300/500';
    certificationDate: string;
    expiryDate?: string;
    certificateUrl?: string;
  };
  images?: {
    logo?: string;
    coverImage?: string;
    gallery: string[];
  };
  videos?: {
    introVideo?: string;
    gallery: string[];
  };
  programs?: {
    teacherTraining: boolean;
    workshops: boolean;
    retreats: boolean;
    onlineClasses: boolean;
  };
  facilities?: string[];
  amenities?: string[];
  pricing?: {
    teacherTraining: {
      price: number;
      currency: string;
      duration: string;
    };
    workshops: {
      price: number;
      currency: string;
      duration: string;
    };
  };
  priceRange?: {
    min: number;
    max: number;
    currency: string;
  };
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    twitter?: string;
  };
  howToReach?: {
    stations: Array<{
      name: string;
      options: Array<{ mode: 'TRAIN' | 'BUS' | 'TAXI' | 'AIR'; details?: string }>;
    }>;
  };
  createdAt: string;
  updatedAt: string;
}

export interface GetSchoolsParams {
  page?: number;
  limit?: number;
  sort?: 'name' | 'rating' | 'price' | 'createdAt';
  order?: 'asc' | 'desc';
  location?: string;
  priceMin?: number;
  priceMax?: number;
  search?: string;
  featured?: boolean;
  style?: string;
  level?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'TEACHER';
  isActive?: boolean;
}

export interface SearchSchoolsParams {
  q?: string;
  location?: string;
  priceMin?: number;
  priceMax?: number;
  style?: string;
  level?: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED' | 'TEACHER';
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface GetSchoolsResponse {
  schools: School[];
  pagination: PaginationInfo;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// School Service
export const schoolService = {
  // Get schools with pagination and filters (public endpoint)
  async getSchools(params: GetSchoolsParams = {}): Promise<ApiResponse<GetSchoolsResponse>> {
    try {
      // Prepare request body for POST request
      const requestBody: any = {};
      
      if (params.page) requestBody.page = params.page;
      if (params.limit) requestBody.limit = params.limit;
      if (params.sort) requestBody.sort = params.sort;
      if (params.order) requestBody.order = params.order;
      if (params.location) requestBody.location = params.location;
      if (params.priceMin !== undefined) requestBody.priceMin = params.priceMin;
      if (params.priceMax !== undefined) requestBody.priceMax = params.priceMax;
      if (params.search) requestBody.search = params.search;
      if (params.featured !== undefined) requestBody.featured = params.featured;
      if (params.style) requestBody.style = params.style;
      if (params.level) requestBody.level = params.level;
      if (params.isActive !== undefined) requestBody.isActive = params.isActive;
      
      // Default to active schools only for public frontend
      if (params.isActive === undefined) {
        requestBody.isActive = true;
      }

      const response = await api.post('/schools', requestBody);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching schools:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch schools');
    }
  },

  // Get school by ID or slug (public endpoint)
  async getSchoolById(idOrSlug: string): Promise<ApiResponse<{ school: School }>> {
    try {
      const response = await api.get(`/schools/${idOrSlug}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching school:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch school');
    }
  },

  // Get featured schools (public endpoint)
  async getFeaturedSchools(limit?: number): Promise<ApiResponse<School[]>> {
    try {
      const queryParams = new URLSearchParams();
      if (limit) queryParams.append('limit', limit.toString());
      
      const url = `/schools/featured${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching featured schools:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch featured schools');
    }
  },

  // Search schools with filters (public endpoint)
  async searchSchools(params: SearchSchoolsParams = {}): Promise<ApiResponse<School[]>> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.q) queryParams.append('q', params.q);
      if (params.location) queryParams.append('location', params.location);
      if (params.priceMin !== undefined) queryParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax !== undefined) queryParams.append('priceMax', params.priceMax.toString());
      if (params.style) queryParams.append('style', params.style);
      if (params.level) queryParams.append('level', params.level);

      const url = `/schools/search${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error('Error searching schools:', error);
      throw new Error(error.response?.data?.message || 'Failed to search schools');
    }
  },

  // Get school statistics (public endpoint)
  async getSchoolStats(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get('/schools/stats');
      return response.data;
    } catch (error: any) {
      console.error('Error fetching school stats:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch school statistics');
    }
  },

  /** Distinct countries (first) and cities for listing filters */
  async getListingLocations(): Promise<{ countries: string[]; cities: string[] }> {
    try {
      const { data } = await api.get<{
        success?: boolean;
        data?: { countries?: string[]; cities?: string[]; locations?: string[] };
      }>('/schools/listing-locations');
      const d = data?.data;
      if (d && Array.isArray(d.countries) && Array.isArray(d.cities)) {
        return { countries: d.countries, cities: d.cities };
      }
      if (d && Array.isArray(d.locations)) {
        return { countries: d.locations, cities: [] };
      }
      return { countries: [], cities: [] };
    } catch (error: any) {
      console.error('Error fetching listing locations:', error);
      return { countries: [], cities: [] };
    }
  },
};


